// A concurrency limiter for lazy per-row loads: at most `concurrency` tasks
// run at once, the rest wait in FIFO order. clear() drops every task that has
// not started (its promise rejects with LimiterClearedError) — the list page
// calls it when the visible rows change, so a page the user has left never
// fires its queued requests. Running tasks are left to finish.

export class LimiterClearedError extends Error {
  constructor() {
    super("Cleared before it started");
    this.name = "LimiterClearedError";
  }
}

export const createLimiter = (concurrency) => {
  if (!(Number.isInteger(concurrency) && concurrency > 0)) {
    throw new RangeError("concurrency must be a positive integer");
  }
  let active = 0;
  const queue = [];

  const pump = () => {
    while (active < concurrency && queue.length > 0) {
      const { task, resolve, reject } = queue.shift();
      active++;
      // A task that throws synchronously still settles its promise and frees
      // its slot.
      new Promise((r) => r(task())).then(resolve, reject).finally(() => {
        active--;
        pump();
      });
    }
  };

  return {
    run: (task) =>
      new Promise((resolve, reject) => {
        queue.push({ task, resolve, reject });
        pump();
      }),
    clear: () => {
      for (const { reject } of queue.splice(0)) {
        reject(new LimiterClearedError());
      }
    },
    active: () => active,
    pending: () => queue.length,
  };
};
